<script setup lang="ts">
import VarsityContentProcessor from "@/components/VarsityContentProcessor.vue"
import { type SelectionTableOrganization } from "@/composables/useOrganization"
import type { Organization } from "@/model/app/organization"
import type { Championship } from "@/model/championship/championship"
import type { ChampionshipEventType } from "@/model/championship/championship-event-type"
import type { ChampionshipPricing } from "@/model/championship/championship-pricing"
import type { Issue } from "@/model/tasks/issue"
import { replacePlaceholders } from "@/utils/placeholders"
import { $currency, $date, useAppConfig, useHttpClient } from "@/vf"
import { useRouter } from "vue-router"

const props = defineProps<{
    event: Championship
    element?: any
}>()

const http = useHttpClient()
const router = useRouter()

const { appConfig } = useAppConfig()

const { data: priceTables } = await http.get<
    {
        id: string
        default: boolean
        prices: ChampionshipPricing
        registrationStatus: Championship["registrationStatus"]
        registrationFrom: string
        registrationUntil: string
        organizations: {
            id: string
            name: string
            allowed: boolean
            subOrganizations: {
                id: string
                name: string
            }[]
        }[]
    }[]
>(`/championship/registration/${props.event.id}/prices`)

async function select(org: Organization | SelectionTableOrganization) {
    const issue = (await http.post<Issue>(`/championship/registration/${props.event.id}/create-issue/${org.id}`)).data
    await router.push({ name: "@Tasks.Customer.index", query: { issue: issue.id } })
}
</script>
<template>
    <div class="event-card-show overflow-hidden mb-4">
        <div class="event-card-show__inner">
            <div class="row justify-content-xl-between">
                <div class="col-12 col-xl-2" :class="{ 'col-xl-12': priceTables.length === 0 }">
                    <h3 class="event-card-show__title">{{ $t("@championship:championship.eventInformation") }}</h3>
                    <div class="row" :class="{ 'd-xl-block': priceTables.length > 0 }">
                        <div
                            class="col-12 col-sm-4"
                            :class="{
                                'col-xl-4': priceTables.length === 0,
                                'col-xl-12 d-xl-block': priceTables.length > 0,
                            }"
                        >
                            <p class="mb-0">
                                <b>{{ $t("@championship:championship.eventType") }}:</b>
                                {{
                                    (event.eventTypes as ChampionshipEventType[] | undefined)
                                        ?.map(c => c.name)
                                        .join(", ")
                                }}
                            </p>
                            <p>
                                <b>{{ $t("@championship:championship.bids") }}:</b>
                                {{ event.bids?.map(c => c.name).join(", ") }}
                            </p>
                        </div>
                        <div
                            class="col-12 col-sm-4"
                            :class="{
                                'col-xl-4': priceTables.length === 0,
                                'col-xl-12 d-xl-block': priceTables.length > 0,
                            }"
                        >
                            <address>
                                {{ event.brandName }}
                                <br />
                                {{ event.brandStreet }} {{ event.brandStreetNumber }}
                                <br />
                                {{ event.brandZip }} {{ event.brandCity }}
                            </address>
                        </div>
                        <div
                            class="col-12 col-sm-4"
                            :class="{
                                'col-xl-4': priceTables.length === 0,
                                'col-xl-12 d-xl-block': priceTables.length > 0,
                            }"
                        >
                            <b>{{ $t("@championship:championship.location") }}:</b>
                            <address>
                                {{ event.location }}
                                <br />
                                {{ event.locationStreet }} {{ event.locationStreetNumber }}
                                <br />
                                {{ event.locationZip }} {{ event.locationCity }}
                            </address>
                        </div>
                    </div>
                </div>

                <template v-for="(table, index) of priceTables" :key="table.id">
                    <div class="col-12 col-xl" :class="{ 'mt-4': index > 0, 'offset-xl-2': index > 0 }">
                        <h3 class="event-card-show__title" v-if="table.default">
                            {{ $t("@championship:championship.price_information") }}
                        </h3>
                        <h3 class="event-card-show__title" v-else>
                            {{
                                $t("@championship:championship.exclusive_price_information_organization", {
                                    name: table.organizations[0].name,
                                })
                            }}
                        </h3>
                        <div class="overflow-auto">
                            <table class="table event-card-show__table">
                                <tbody>
                                    <tr>
                                        <th></th>
                                        <th class="text-right">
                                            {{ $t("@championship:championship.prices.early_rate") }}
                                        </th>
                                        <th class="text-right">
                                            {{ $t("@championship:championship.prices.normal_rate") }}
                                        </th>
                                    </tr>
                                    <tr>
                                        <td>{{ $t("@championship:championship.prices.active_participant") }}</td>
                                        <td class="text-right">{{ $currency(table.prices.priceEarlyActiveCents) }}</td>
                                        <td class="text-right">{{ $currency(table.prices.priceNormalActiveCents) }}</td>
                                    </tr>
                                    <tr>
                                        <td>{{ $t("@championship:championship.prices.plus_category") }}</td>
                                        <td class="text-right">
                                            {{ $currency(table.prices.priceEarlyAdditionalCategoryCents) }}
                                        </td>
                                        <td class="text-right">
                                            {{ $currency(table.prices.priceNormalAdditionalCategoryCents) }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>{{ $t("@championship:championship.prices.coach") }}</td>
                                        <td class="text-right">{{ $currency(table.prices.priceEarlyCoachCents) }}</td>
                                        <td class="text-right">{{ $currency(table.prices.priceNormalCoachCents) }}</td>
                                    </tr>
                                    <tr>
                                        <td>{{ $t("@championship:championship.prices.escort") }}</td>
                                        <td class="text-right">{{ $currency(table.prices.priceEarlyEscortCents) }}</td>
                                        <td class="text-right">{{ $currency(table.prices.priceNormalEscortCents) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="col-12 col-xl" :class="{ 'mt-4': index > 0 }">
                        <div class="event-card-show__registration" v-if="table.default">
                            <h3 class="event-card-show__title">
                                {{ $t("@championship:championship.registration") }}
                            </h3>
                            <div class="row my-2">
                                <div class="col-md-6">
                                    <p>
                                        <template v-if="!event.registrationDateText">
                                            <time :datetime="table.registrationFrom">
                                                {{ $date(table.registrationFrom) }}
                                            </time>
                                            -
                                            <time :datetime="table.registrationUntil">
                                                {{ $date(table.registrationUntil) }}
                                            </time>
                                        </template>
                                        <template v-else>
                                            <VarsityContentProcessor
                                                :content="
                                                    replacePlaceholders(event.registrationDateText, {
                                                        from: $date(table.registrationFrom),
                                                        until: $date(table.registrationUntil),
                                                    })
                                                "
                                            />
                                        </template>
                                    </p>
                                </div>

                                <div class="col-md-6 text-sm-right" v-if="!element.config.hideLink">
                                    <RouterLink
                                        :to="event.landingPage.path"
                                        class="btn btn-primary"
                                        v-if="event.landingPage"
                                    >
                                        {{ $t("@championship:championship.landingPage.link") }}
                                    </RouterLink>
                                </div>
                            </div>

                            <template v-if="table.registrationStatus == 'open'">
                                <h4 class="event-card-show__title mt-4">
                                    {{ $t("@championship:championship.organization") }}
                                </h4>
                                <div v-for="organization in table.organizations" :key="organization.id">
                                    <div class="row my-2">
                                        <div class="col-md-6">{{ organization.name }}</div>
                                        <div class="col-md-6 text-sm-right">
                                            <button
                                                class="btn btn-primary btn-sm mt-2 mt-sm-0"
                                                @click="select(organization)"
                                                :disabled="!organization.allowed"
                                            >
                                                {{
                                                    $t(
                                                        event.waitingListEnabled
                                                            ? "@championship:registration.window.organization.button.waiting-list"
                                                            : "@championship:registration.window.organization.button",
                                                    )
                                                }}
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        class="row"
                                        v-for="subOrganization in organization.subOrganizations"
                                        :key="subOrganization.id"
                                    >
                                        <div class="col-md-6 pl-5">
                                            {{ subOrganization.name }}
                                        </div>
                                        <div class="col-md-6 text-sm-right">
                                            <button class="btn btn-primary btn-sm" @click="select(subOrganization)">
                                                {{
                                                    $t(
                                                        event.waitingListEnabled
                                                            ? "@championship:registration.window.organization.button.waiting-list"
                                                            : "@championship:registration.window.organization.button",
                                                    )
                                                }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div class="event-card-show__registration d-none d-lg-block" v-if="!table.default">
                            <h3 class="event-card-show__title">
                                {{ $t("@championship:championship.registration") }}
                            </h3>
                            <p>
                                <template v-if="!event.registrationDateText">
                                    <time :datetime="table.registrationFrom">
                                        {{ $date(table.registrationFrom) }}
                                    </time>
                                    -
                                    <time :datetime="table.registrationUntil">
                                        {{ $date(table.registrationUntil) }}
                                    </time>
                                </template>
                                <template v-else>
                                    <VarsityContentProcessor
                                        :content="
                                            replacePlaceholders(event.registrationDateText, {
                                                from: $date(table.registrationFrom),
                                                until: $date(table.registrationUntil),
                                            })
                                        "
                                    />
                                </template>
                            </p>

                            <template v-if="table.registrationStatus == 'open'">
                                <template v-for="organization in table.organizations" :key="organization.id">
                                    <h4 class="event-card-show__title mt-4">
                                        {{ $t("@championship:championship.organization") }}
                                    </h4>
                                    <div class="row my-2">
                                        <div class="col-md-6">{{ organization.name }}</div>
                                        <div class="col-md-6 text-right">
                                            <button class="btn btn-primary btn-sm" @click="select(organization)">
                                                {{
                                                    $t(
                                                        event.waitingListEnabled
                                                            ? "@championship:registration.window.organization.button.waiting-list"
                                                            : "@championship:registration.window.organization.button",
                                                    )
                                                }}
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        class="row"
                                        v-for="subOrganization in organization.subOrganizations"
                                        :key="subOrganization.id"
                                    >
                                        <div class="col-md-6 pl-5">
                                            {{ subOrganization.name }}
                                        </div>
                                        <div class="col-md-6 text-right">
                                            <button class="btn btn-primary btn-sm" @click="select(subOrganization)">
                                                {{
                                                    $t(
                                                        event.waitingListEnabled
                                                            ? "@championship:registration.window.organization.button.waiting-list"
                                                            : "@championship:registration.window.organization.button",
                                                    )
                                                }}
                                            </button>
                                        </div>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>
                    <div class="w-100"></div>
                </template>
            </div>
            <div class="alert alert-primary d-inline-block" v-if="!appConfig.login">
                {{ $t("@championship:championship.login_hint") }}
            </div>
            <div
                class="alert alert-primary d-inline-block"
                v-if="
                    appConfig.login &&
                    priceTables.every(table => !table.organizations || table.organizations.length === 0)
                "
            >
                {{ $t("@championship:championship.organization_needed_hint") }}
            </div>
        </div>
    </div>
</template>
